<script setup>
import ArgonCounterBadge from "@/components/ArgonCounterBadge.vue";
import { buildPlanMapRouteTooltipHtml, buildPlanMapStopTooltipHtml } from "./planMapTooltip";

const props = defineProps({
  routeItem: {
    type: Object,
    required: true,
  },
  hourItems: {
    type: Array,
    default() {
      return [];
    },
  },
  isVisible: {
    type: Boolean,
    default: true,
  },
  visibleStops: {
    type: Array,
    default() {
      return [];
    },
  },
  stopStyleResolver: {
    type: Function,
    default() {
      return () => ({});
    },
  },
});

const emit = defineEmits(["toggle-visibility", "tooltip-show", "tooltip-hide"]);

function onToggleVisibility() {
  emit("toggle-visibility", props.routeItem.id);
}

function getStopInlineStyle(stopItem) {
  return props.stopStyleResolver(stopItem);
}

function showTooltip(html, event) {
  const anchorEl = event?.currentTarget;
  if (!anchorEl) return;

  emit("tooltip-show", {
    anchorEl,
    html,
  });
}

function showStopTooltip(stopItem, event) {
  hideTooltip();
  showTooltip(buildPlanMapStopTooltipHtml(props.routeItem, stopItem), event);
}

function showRouteTooltip(event) {
  hideTooltip();
  showTooltip(buildPlanMapRouteTooltipHtml(props.routeItem), event);
}

function hideTooltip() {
  emit("tooltip-hide");
}
</script>

<template>
  <article class="timeline-row">
    <button
      type="button"
      class="route-info"
      :class="{ 'is-hidden': !isVisible }"
      :aria-pressed="isVisible"
      @click="onToggleVisibility"
      @mouseenter.stop="showRouteTooltip"
      @focus.stop="showRouteTooltip"
      @mouseleave="hideTooltip"
      @blur="hideTooltip"
    >
      <div class="route-text">
        <p class="route-name" :class="{ 'is-hidden': !isVisible }">
          {{ routeItem.name }}
        </p>
        <p v-if="routeItem.plate" class="route-meta">
          <span class="route-meta-text">{{ routeItem.plate }}</span>
        </p>
      </div>
      <i
        class="route-visibility-icon"
        :class="isVisible ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'"
        aria-hidden="true"
      ></i>
    </button>

    <div class="route-track-wrap" :class="{ 'is-hidden': !isVisible }">
      <div class="route-track-surface">
        <span class="route-track-line" :style="{ backgroundColor: isVisible ? routeItem.color : '#6b7280' }"></span>
        <span
          v-for="hourItem in hourItems"
          :key="`guide_${routeItem.id}_${hourItem.minute}`"
          class="row-hour-guide"
          :style="{ left: `${hourItem.leftPercent}%` }"
        ></span>

        <button
          v-for="stopItem in visibleStops"
          :key="stopItem.key"
          type="button"
          class="stop-chip"
          :style="getStopInlineStyle(stopItem)"
          :aria-label="`Parada ${stopItem.orderLabel}. Estado ${stopItem.statusLabel}`"
          @mouseenter="showStopTooltip(stopItem, $event)"
          @mouseleave="hideTooltip"
          @focus="showStopTooltip(stopItem, $event)"
          @blur="hideTooltip"
        >
          <argon-counter-badge
            :value="stopItem.orderLabel"
            :type="stopItem.statusTone"
            size="sm"
          />
        </button>
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss">
.timeline-row {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe2ee;
  border-radius: 9px;
  display: grid;
  gap: 6px;
  grid-template-columns: var(--timeline-left-col-width, 140px) minmax(0, 1fr);
  min-height: 34px;
  overflow: visible;
  padding: 4px 6px;
  position: relative;
  transition: background 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease;
}

.timeline-row:hover {
  border-color: #c5d0de;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
}

.timeline-row:hover .row-hour-guide {
  border-left-color: rgba(100, 116, 139, 0.34);
}

.route-info {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 6px;
  justify-content: flex-start;
  grid-column: 1;
  min-width: 0;
  padding: 0;
  position: relative;
  text-align: left;
  width: 100%;
  z-index: 4;
}

.route-info:focus-visible {
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.18);
  outline: none;
}

.route-info.is-hidden .route-text {
  opacity: 0.78;
}

.route-visibility-icon {
  color: #94a3b8;
  flex: 0 0 auto;
  font-size: 0.65rem;
  opacity: 0;
  transition: opacity 0.14s ease, color 0.14s ease;
}

.route-info:hover .route-visibility-icon,
.route-info:focus-visible .route-visibility-icon {
  opacity: 1;
}

.route-info.is-hidden .route-visibility-icon {
  color: #94a3b8;
  opacity: 1;
}

.route-text {
  flex: 1 1 auto;
  min-width: 0;
}

.route-name {
  color: #1f2937;
  font-size: 0.70rem;
  font-weight: 600;
  line-height: 1.1;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.route-name.is-hidden {
  color: #94a3b8;
}

.route-meta {
  color: #7a8aa0;
  display: flex;
  gap: 6px;
  line-height: 1;
  margin: 1px 0 0;
  min-width: 0;
}

.route-meta-text {
  font-size: 0.53rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.route-track-wrap {
  grid-column: 2;
  min-height: 24px;
  min-width: 0;
  overflow: visible;
  position: relative;
  z-index: 1;
}

.route-track-surface {
  background: transparent;
  border-radius: 999px;
  height: 24px;
  isolation: isolate;
  overflow: visible;
  position: relative;
}

.route-track-surface::before {
  background: #d8e2ec;
  border-radius: 999px;
  content: "";
  height: 3px;
  left: 0;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.route-track-wrap.is-hidden .row-hour-guide {
  opacity: 0.22;
}

.route-track-line {
  border-radius: 999px;
  height: 3px;
  left: 0;
  opacity: 0.9;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.route-track-wrap.is-hidden .route-track-line {
  opacity: 0.32;
}

.row-hour-guide {
  border-left: 1px solid rgba(100, 116, 139, 0.38);
  bottom: 3px;
  position: absolute;
  top: 3px;
  transform: translateX(-50%);
}

.stop-chip {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  line-height: 1;
  min-width: 30px;
  overflow: visible;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform 0.14s ease, box-shadow 0.14s ease;
  width: 30px;
  z-index: 2;
}

.stop-chip:hover {
  transform: translate(-50%, -50%) scale(1.08);
  z-index: 6;
}

.stop-chip:focus-visible {
  border-radius: 999px;
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.18);
  outline: none;
  z-index: 6;
}

.stop-chip :deep(.argon-counter-badge) {
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.16);
  pointer-events: none;
}
</style>
